import { FirestoreDoc } from "@p0security/frontend-core/react";
import { Evidence } from "@p0security/shared/types/evidence";
import { Pagination, Spacing } from "@p0security/ui";
import { usePagination } from "hooks/usePagination";
import { useMemo } from "react";

import { TitledPreApprovalList } from "../components/PreApprovalList";
import { RequestFilters } from "../components/RequestFilters";
import { useActiveAndPendingPagination } from "../hooks/useActiveAndPendingPagination";
import { useFilteredData } from "../hooks/useFilteredData";
import { useActivePreApproval } from "../hooks/usePreApproval";
import { usePreApprovalFilterOptions } from "../hooks/useRequestFilterOptions";
import { PRE_APPROVAL_FILTER_CONFIG } from "../utils/filterConfigs";

export const ActivePreApproval: React.FC = () => {
  // TODO: filter out pre approval that is not relevant to the user
  const activePreApproval = useActivePreApproval();

  const standingPreApproval = useMemo(
    () => activePreApproval.list.filter((e) => e.data.startsAt <= Date.now()),
    [activePreApproval.list]
  );

  const pendingPreApproval = useMemo(
    () => activePreApproval.list.filter((e) => e.data.startsAt > Date.now()),
    [activePreApproval.list]
  );

  // Combine all pre-approvals so all can be filtered together
  const allPreApprovals = useMemo(
    () => [...standingPreApproval, ...pendingPreApproval],
    [standingPreApproval, pendingPreApproval]
  );

  const options = usePreApprovalFilterOptions(allPreApprovals);

  const { filters, applyFilters, resetFilters } = useFilteredData<
    FirestoreDoc<Evidence>
  >(PRE_APPROVAL_FILTER_CONFIG);

  const filteredStandingPreApproval = useMemo(
    () => applyFilters(standingPreApproval),
    [standingPreApproval, applyFilters]
  );

  const filteredPendingPreApproval = useMemo(
    () => applyFilters(pendingPreApproval),
    [pendingPreApproval, applyFilters]
  );

  const [pagination, _, { handlePageChange }] = usePagination();
  const page = pagination.page ?? 1;
  const pageSize = pagination.pageSize ?? 10;

  // Apply pagination to each section independently
  const {
    paginatedActiveSection: paginatedStandingPreApproval,
    paginatedPendingSection: paginatedPendingPreApproval,
    totalItems,
  } = useActiveAndPendingPagination(
    filteredStandingPreApproval,
    filteredPendingPreApproval,
    { page, pageSize }
  );

  const showAnyPreApprovals = useMemo(
    () =>
      filteredStandingPreApproval.length > 0 ||
      filteredPendingPreApproval.length > 0,
    [filteredStandingPreApproval, filteredPendingPreApproval]
  );

  return (
    <div style={{ maxWidth: 1500 }}>
      <RequestFilters
        filters={filters}
        options={options}
        hideApprovalDateFilter={true}
        hideExport={true}
        resetFilters={resetFilters}
      />
      <div style={{ minHeight: "22em" }}>
        <TitledPreApprovalList
          title="Active"
          preApproval={paginatedStandingPreApproval}
          loading={activePreApproval.loading}
        />
      </div>
      <div style={{ minHeight: "22em" }}>
        <TitledPreApprovalList
          title="Pending"
          preApproval={paginatedPendingPreApproval}
          loading={activePreApproval.loading}
        />
      </div>
      {showAnyPreApprovals && (
        <Spacing asChild mt={16}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={totalItems}
            size="small"
            showSizeChanger
            onChange={handlePageChange}
            className="text-right"
          />
        </Spacing>
      )}
    </div>
  );
};
